import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { connectorCartProductsSelector, connectorAfterCreateClaimRecord } from '@redux/slices/connectorSlice'
import { claimRecordIdSelector } from '@redux/slices/claimStatusSlice'
import {
  useCreateClaimRecordMutation,
  useLazyGetClaimRecordQuery,
  useUpdateClaimRecordMutation,
} from '@redux/queries/claimRecords'
import { Customer } from '@typesApp/Customer'
import { ClaimData } from '@typesApp/ClaimData'
import { CartBundle } from '@typesApp/Product'
import { ClaimRecord, CreateClaimRecordPayload, CreateClaimRecordResult } from '@typesApp/ClaimRecord'
import { isGatewayTimeout } from '@utils/errorUtil'
import { formatCommerceCode } from '@utils/formatCommerceCode'
import { useEnvironment } from './useEnvironment'
import { useSessionInfo } from './useSessionInfo'
import { useActiveData } from './useActiveData'
import { useCustomer } from './useCustomer'
import { useCreateClaimRecordStatus } from './useCreateClaimRecordStatus'
import { useCart } from './useCart'

type CreateOptions = {
  insurerId?: string
  cartBundle?: CartBundle
  skipActiveDataCheck?: boolean
  customer?: Customer
}

type CreateClaimRecordParams = {
  customer: Customer
  options?: CreateOptions
}

type UseCreateClaimRecordReturn = {
  isError: boolean
  createClaimRecord: (options?: CreateOptions) => Promise<CreateClaimRecordResult> | never
  patchClaimRecord: (claimData: ClaimData, id?: string) => Promise<ClaimRecord>
}

export const useCreateClaimRecord = (): UseCreateClaimRecordReturn => {
  const dispatch = useDispatch()
  const { ecommerceCode, ecommerceTransactionHost, lang, country } = useEnvironment()

  const claimRecordId = useSelector(claimRecordIdSelector)
  const { isGuestUser, customerContext } = useSessionInfo()

  const { fetchCartBundle } = useCart()
  const { fetchActiveData } = useActiveData()
  const { validateCustomerOrThrow } = useCustomer()
  const { setCreateClaimRecordError } = useCreateClaimRecordStatus()

  const [fetchClaimRecord] = useLazyGetClaimRecordQuery()
  const [triggerCreate, { isError: isCreateError }] = useCreateClaimRecordMutation()
  const [triggerPatch, { isError: isUpdateError }] = useUpdateClaimRecordMutation()

  const fallbackCartId = customerContext?.cartId
  const isError = isCreateError || isUpdateError

  const getDefaultPayload = useCallback(
    (customer: Customer, options?: CreateOptions): CreateClaimRecordPayload => {
      let payload: CreateClaimRecordPayload = {
        customerNumber: customer?.id,
        customerEmail: customer?.email,
        customerPhone: customer?.phoneNumber,
        customerName: {
          firstName: customer?.firstName,
          lastName: customer?.lastName,
        },
        eligibleProducts: [],
        locale: `${lang}_${country?.toUpperCase()}`,
        ecommerce: formatCommerceCode({
          ecommerceCode,
          ecommerceTransactionHost,
        }),
      }

      if (isGuestUser) {
        delete payload.customerName
        delete payload.customerEmail
        delete payload.customerPhone
      }

      // do not send empty name
      const emptyName = !customer?.firstName && !customer?.lastName
      if (emptyName) {
        delete payload.customerName
      }

      if (options?.insurerId) {
        payload = {
          ...payload,
          claimData: {
            primaryCoverage: {
              insurer: {
                id: options?.insurerId ?? '',
              },
            },
          },
        }
      }

      return payload
    },
    [ecommerceCode, ecommerceTransactionHost, lang, country, isGuestUser]
  )

  const createClaimRecordOrThrow = useCallback(
    async (customer: Customer, payload: CreateClaimRecordPayload): Promise<ClaimRecord> => {
      try {
        return await triggerCreate(payload).unwrap()
      } catch (error) {
        if (isGatewayTimeout(error)) {
          const activeData = await fetchActiveData(customer?.id)
          if (activeData?.id) {
            return await fetchClaimRecord(activeData.id).unwrap()
          }
        }
        throw error
      }
    },
    [triggerCreate, fetchActiveData, fetchClaimRecord]
  )

  const createClaimRecordDefault = useCallback(
    async (params: CreateClaimRecordParams): Promise<CreateClaimRecordResult> => {
      const { customer, options } = params
      const payload = getDefaultPayload(customer, options)
      const result = await createClaimRecordOrThrow(customer, payload)
      dispatch(connectorAfterCreateClaimRecord())
      return result
    },
    [dispatch, getDefaultPayload, createClaimRecordOrThrow]
  )

  const getPayloadFromCartItems = useCallback(
    (customer: Customer, cartBundle: CartBundle, options?: CreateOptions): CreateClaimRecordPayload => {
      const payload: CreateClaimRecordPayload = {
        ...getDefaultPayload(customer, options),

        // Note: cartBundle.cartId may be undefined for
        // empty cart but commerce F/E provided a cartId
        cartId: cartBundle.cartId || fallbackCartId,
        eligibleProducts: cartBundle.products ?? [],
        orderSubtotal: cartBundle.orderSubtotal,
        shippingCosts: cartBundle.shippingCosts,
      }

      // empty string cartId not accepted on b/e
      if (!payload.cartId) {
        delete payload.cartId
      }

      return payload
    },
    [getDefaultPayload, fallbackCartId]
  )

  const createClaimRecordWithCartItems = useCallback(
    async (params: CreateClaimRecordParams): Promise<CreateClaimRecordResult> => {
      const { customer, options } = params
      let cartBundle = options?.cartBundle

      // fetch cart items unless already provided in options
      if (!cartBundle) {
        try {
          cartBundle = await fetchCartBundle()
        } catch (error) {
          // create claim record w/o cart products on cart items error
          return await createClaimRecordDefault(params)
        }
      }

      const payload = getPayloadFromCartItems(customer, cartBundle, options)
      const result = await createClaimRecordOrThrow(customer, payload)
      dispatch(connectorAfterCreateClaimRecord(cartBundle))
      return result
    },
    [dispatch, fetchCartBundle, createClaimRecordDefault, getPayloadFromCartItems, createClaimRecordOrThrow]
  )

  /**
   * Create new claim record or return active one if exists
   */
  const createClaimRecord = useCallback(
    async (options?: CreateOptions): Promise<CreateClaimRecordResult> | never => {
      try {
        setCreateClaimRecordError(false)

        // skip validate customer if caller provides customer object
        const customer = options?.customer ?? (await validateCustomerOrThrow())

        if (!options?.skipActiveDataCheck) {
          // get active to ensure we do not create multiple active claim records for customer
          const activeData = await fetchActiveData(customer.id)
          if (activeData?.id) {
            return { id: activeData.id }
          }
        }

        return createClaimRecordWithCartItems({ customer, options })
      } catch (error) {
        setCreateClaimRecordError(true)
        throw error
      }
    },
    [setCreateClaimRecordError, fetchActiveData, createClaimRecordWithCartItems, validateCustomerOrThrow]
  )

  /**
   * Patch claim record - the provided id has precedence over the claimRecordId from redux state
   * The id parameter is useful when we need to create claim record and then immediately patch.
   */
  const patchClaimRecord = useCallback(
    (claimData: ClaimData, id?: string): Promise<ClaimRecord> => {
      return triggerPatch({
        id: id || claimRecordId,
        claimData,
      }).unwrap()
    },
    [triggerPatch, claimRecordId]
  )

  return {
    isError,
    createClaimRecord,
    patchClaimRecord,
  }
}
